import { useContext, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { Toast } from 'design-system/src/components/Toast/Toast';

import type { TaskEvent, OutboundTaskType } from '../../../../api';
import type { TremfyaExpressEnrollmentTask, WithAdditionalTaskResponseFields } from '../../types';
import { TaskContext } from '../../contexts';
import DeclineModal from '../../DeclineModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import TremfyaExpressEnrollmentCoreForm from '../../tremfyaExpressEnrollmentTask/TremfyaExpressEnrollmentCoreForm';
import { RedraftBanner, ValuePropBanner } from '../../tremfyaExpressEnrollmentTask/sharedStyles';
import TaskInstructionSummary from '../../shared/TaskInstructionSummary';
import { TaskCompletionEventBanner } from '../../shared';
import { StructuredTaskViewWrapper } from '..';

/**
 * Version of TaskView that is used for Tremfya Express Enrollment tasks.
 */
const TremfyaExpressEnrollmentTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<TremfyaExpressEnrollmentTask>;
}> = ({ taskResponse }) => {
  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [showNewFollowupTaskModal, setShowNewFollowupTaskModal] = useState(false);
  // tracking submission state manually because the actual submit mutation is in a child component
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const isCompleted = task.status === 'completed';
  const redraftUrl = `/tremfya-enrollment/${task.url_token}`;

  const onSuccess = () => {
    onTaskUpdate();
    toast.custom(() => <Toast variant="success">Enrollment submitted</Toast>);
    navigate('/tasks');
  };

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline && task.timeline.length > 1) {
      const eventItem = task.timeline[1];
      if (eventItem.item_type === 'event') {
        completionEvent = eventItem as TaskEvent;
      }
    }
    if (!completionEvent) {
      Sentry.captureMessage('No completion event found for Tremfya Express Enrollment task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    }
  }

  const declineButton = (
    <Button
      type="button"
      size="sm"
      disabled={isSubmitting}
      variant="tertiary"
      onClick={() => setDeclineModalOpen(true)}
    >
      Decline enrollment
    </Button>
  );

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={task.type as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        sidebar={{
          drug: { data: drug, showBusinessRelationshipNotice: true },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        <ValuePropBanner>
          <Typography>This is a digital enrollment form for a patient support program for Tremfya.</Typography>
        </ValuePropBanner>

        <TaskInstructionSummary className="!mb-6">
          Please complete the digital form below to enroll your patient in the support program, which provides nurse
          guide support and access to a copay savings card, if eligible. Note this form does not require any signatures.
        </TaskInstructionSummary>

        {isCompleted && (
          <>
            <TaskCompletionEventBanner completionEvent={completionEvent} />

            {task.show_redraft_banner && (
              <RedraftBanner>
                <Typography styledAs="bodySmallDMSans" weight={500}>
                  Something went wrong while processing the submission below. An editable copy of the last form has been
                  prepared for you to revise and resubmit.
                </Typography>
                <Button size="sm" onClick={() => navigate(redraftUrl)}>
                  Revise submission
                </Button>
              </RedraftBanner>
            )}
          </>
        )}

        <TremfyaExpressEnrollmentCoreForm
          drug={drug}
          prescriber={prescriber}
          formData={task.form_data}
          declineButton={declineButton}
          setShowNewFollowupTaskModal={setShowNewFollowupTaskModal}
          onSuccess={onSuccess}
          setIsSubmitting={setIsSubmitting}
          submissionEndpoint={`tasks/${taskId}/complete-tremfya-enrollment`}
          isCompleted={isCompleted}
        />
      </StructuredTaskViewWrapper>

      {declineModalOpen && (
        <DeclineModal
          closeModal={() => setDeclineModalOpen(false)}
          patient={patient}
          drug={drug}
          taskKey={task.task_key}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {showNewFollowupTaskModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowNewFollowupTaskModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key}
          relatedTaskType={task.type}
        />
      )}
    </>
  );
};

export default TremfyaExpressEnrollmentTaskView;
